import { Box, Container, Typography } from "@mui/material";
import { heroThemes, palette5, tokens } from "../theme";

export function Section({ children, tone = "cream", sx, narrow = false, id }) {
  const bg = { cream: tokens.cream, white: tokens.paper, sage: tokens.sage, dark: tokens.forestDark, navy: palette5.navy[0] }[tone];
  const dark = tone === "dark" || tone === "navy";
  return (
    <Box id={id} component="section" sx={{ background: bg, color: dark ? "#fff" : "inherit", py: { xs: 7, md: 10 }, ...sx }}>
      <Container maxWidth={narrow ? "md" : "lg"}>{children}</Container>
    </Box>
  );
}

export function Eyebrow({ children, light }) {
  return (
    <Typography sx={{ fontSize: 13, fontWeight: 500, letterSpacing: ".14em", textTransform: "uppercase", color: light ? palette5.basalt[4] : tokens.forest, mb: 1.5 }}>
      {children}
    </Typography>
  );
}

/** Frosted-glass card used on top of the gradient hero panels. */
export function Glass({ children, sx, light = false }) {
  return (
    <Box
      sx={{
        background: light ? "rgba(246,243,234,.86)" : "rgba(255,255,255,.13)",
        color: light ? tokens.ink : "#fff",
        border: `1px solid ${light ? "rgba(255,255,255,.7)" : "rgba(255,255,255,.28)"}`,
        backdropFilter: "blur(14px)",
        borderRadius: "22px",
        p: 2.5,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

/**
 * Big rounded gradient panel at the top of every page.
 * `actions` is a node (buttons); `visual` is an optional node shown on the right on wide screens.
 * The floating menu pill sits over the top of this panel, so it reserves space for it.
 */
export function HeroPanel({ theme = "basalt", eyebrow, title, subtitle, actions, visual, minHeight = 600 }) {
  const t = heroThemes[theme] || heroThemes.basalt;
  return (
    <Box sx={{ px: { xs: 1.25, md: 3 }, pt: { xs: 1.25, md: 2 } }}>
      <Box
        sx={{
          background: t.bg, color: "#fff", borderRadius: { xs: "24px", md: "34px" }, overflow: "hidden", position: "relative",
          minHeight: { xs: 0, md: minHeight }, pt: { xs: 14, md: 22 }, pb: { xs: 6, md: 9 }, display: "flex", alignItems: "center",
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: "grid", gap: { xs: 5, md: 6 }, gridTemplateColumns: { xs: "1fr", md: visual ? "1fr 1.05fr" : "1fr" }, alignItems: "center" }}>
            <Box>
              {eyebrow && <Typography sx={{ fontSize: 14, fontWeight: 500, letterSpacing: ".1em", textTransform: "uppercase", color: t.eyebrow, mb: 2 }}>{eyebrow}</Typography>}
              <Typography variant="h1" sx={{ fontSize: { xs: 40, md: 64 }, lineHeight: 1.06, maxWidth: 640 }}>{title}</Typography>
              {subtitle && <Typography sx={{ mt: 3, fontSize: { xs: 16.5, md: 19 }, opacity: 0.88, maxWidth: 520, fontWeight: 300 }}>{subtitle}</Typography>}
              {actions && <Box sx={{ mt: 4.5, display: "flex", gap: 1.5, flexWrap: "wrap" }}>{actions}</Box>}
            </Box>
            {visual && <Box>{visual}</Box>}
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

// Kept for pages that still pass eyebrow/title/subtitle: renders the gradient panel.
export function PageHero({ eyebrow, title, subtitle, children, theme = "basalt", actions }) {
  return <HeroPanel theme={theme} eyebrow={eyebrow} title={title} subtitle={subtitle} actions={actions || children} minHeight={440} />;
}

/** Hero button styles that match each panel theme. */
export function heroButtonSx(theme, kind = "solid") {
  const t = heroThemes[theme] || heroThemes.basalt;
  if (kind === "ghost") {
    return { color: "#fff", border: "1px solid rgba(255,255,255,.55)", background: "rgba(255,255,255,.08)", "&:hover": { background: "rgba(255,255,255,.18)", borderColor: "#fff" } };
  }
  return { background: t.cta, color: t.ctaText || "#fff", "&:hover": { background: t.ctaHover } };
}

// Responsive grid helper without relying on MUI Grid API details.
export function Cards({ min = 260, gap = 3, children, sx }) {
  return (
    <Box sx={{ display: "grid", gap, gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`, ...sx }}>
      {children}
    </Box>
  );
}

export function Card({ children, sx }) {
  return (
    <Box sx={{ background: tokens.paper, border: `1px solid ${tokens.line}`, borderRadius: "24px", p: 3.5, ...sx }}>
      {children}
    </Box>
  );
}

/** Headline with an italic, oxide-colored second line: <Headline top="Better products." em="Higher standards." /> */
export function Headline({ top, em, light = false, center = false, size = { xs: 38, md: 56 }, sx }) {
  return (
    <Typography variant="h2" component="h2" sx={{ fontSize: size, lineHeight: 1.1, textAlign: center ? "center" : "left", color: light ? "#fff" : tokens.ink, ...sx }}>
      {top}
      {em && <><br /><Box component="span" sx={{ fontStyle: "italic", color: light ? "#fff" : tokens.oxide }}>{em}</Box></>}
    </Typography>
  );
}

/** Small spaced-out label above a headline. */
export function Kicker({ children, color, center = false, sx }) {
  return (
    <Typography sx={{ fontSize: 12.5, fontWeight: 500, letterSpacing: ".14em", textTransform: "uppercase", color: color || tokens.muted, mb: 2, textAlign: center ? "center" : "left", ...sx }}>
      {children}
    </Typography>
  );
}

/** Pill buttons from the design: oxide, moss, outline-on-dark and cream. */
export const pillSx = {
  oxide: { background: tokens.oxide, color: "#fff", px: 3, py: 1.25, fontSize: 16, "&:hover": { background: tokens.oxideHover } },
  moss: { background: tokens.mossBtn, color: "#fff", px: 3, py: 1.25, fontSize: 16, "&:hover": { background: "#4F5B43" } },
  ghostLight: { color: "#fff", border: "1px solid rgba(255,255,255,.8)", px: 3, py: 1.25, fontSize: 16, "&:hover": { background: "rgba(255,255,255,.14)", borderColor: "#fff" } },
  cream: { background: tokens.cream, color: tokens.forestDark, px: 3, py: 1.25, fontSize: 16, "&:hover": { background: "#fff" } },
};
